import {
  Alert,
  AlertType,
  Button,
  TooltipTrigger,
} from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { useScheduleDrawerUrlSync } from '~/hooks/use-schedule-drawer-url-sync/useScheduleDrawerUrlSync';
import { useListScheduledQueries } from '~/react-query-hooks/alfred/useScheduledQueries';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '~/stores/use-flags/useFlags';
import { ToastPosition, useToasts } from '~/stores/use-toasts/useToasts';
import { Feedback } from '~/views/alfred/quick-analysis/use-chat/assistant-response.type';
import { CopyConversation } from '~/views/alfred/research/copy-conversation/CopyConversation';
import { MAX_SCHEDULED_TASKS } from '~/views/alfred/research/history-sidebar/HistorySidebar';
import { PrintPdfBtn } from '~/views/alfred/research/print-pdf-btn/PrintPdfBtn';
import { ShareTaskBtn } from '~/views/alfred/research/share-task-btn/ShareTaskBtn';
import { ConversationTask } from '~/views/alfred/research/use-chat/use-chat.types';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import styles from './ChatFeedbackButtons.module.scss';

const TOAST_ID = 'chat-feed-back';
export const CHAT_FEEDBACK_ID = 'chat-feedback';

const isFeedbackValue = (value: string): value is Feedback =>
  Object.values(Feedback).some(v => v === value);

export interface ChatFeedbackButtonsProps {
  addFeedback: (feedback: Feedback) => void;
  onBadFeedback?: () => void;
  feedback?: 'Good' | 'Bad' | 'Neutral';
  task?: ConversationTask;
  showingSteps?: boolean;
  conversationId?: string;
}

export const ChatFeedbackButtons = ({
  addFeedback,
  onBadFeedback,
  feedback,
  task,
  showingSteps = false,
  conversationId,
}: ChatFeedbackButtonsProps) => {
  const { t } = useTranslation('chat');
  const { showToast } = useToasts();
  const { trackAlfredFeedback } = usePosthogEvents();
  const { open: openScheduleDrawer } = useScheduleDrawerUrlSync();
  const webSearch = useChat(state => state.webSearch);

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );

  const { data: scheduledQueriesData, isLoading: isScheduledQueriesLoading } =
    useListScheduledQueries({ status: 'active' }, isScheduledTaskEnabled);
  // Treat an unknown count (still loading) as at the limit to avoid a first-load
  // window where the button is enabled for users already at the cap.
  const isAtMaxScheduledTasks =
    isScheduledTaskEnabled &&
    (isScheduledQueriesLoading ||
      (scheduledQueriesData?.total_count ?? 0) >= MAX_SCHEDULED_TASKS);

  const onFeedbackButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const feedback = e.currentTarget.name;

    if (feedback === Feedback.Good) {
      trackAlfredFeedback(POSTHOG_TRACKING_SOURCES.ALFRED, 'thumbs_up');
    } else if (feedback === Feedback.Bad) {
      trackAlfredFeedback(POSTHOG_TRACKING_SOURCES.ALFRED, 'thumbs_down');
    }

    if (isFeedbackValue(feedback)) {
      addFeedback(feedback);
    }

    if (feedback === Feedback.Bad && onBadFeedback) {
      onBadFeedback();
    }

    if (feedback === Feedback.Good) {
      showToast({
        id: TOAST_ID,
        hideToast: true,
        position: ToastPosition.TopRight,
        content: (
          <Alert
            type={AlertType.Success}
            message={t('chat:feedback')}
            withIcon
          />
        ),
      });
    }
  };

  return (
    <div
      className={styles.buttonsWrapper}
      id={CHAT_FEEDBACK_ID}
      data-testid={CHAT_FEEDBACK_ID}
    >
      {task && conversationId && (
        <>
          <ShareTaskBtn
            conversationId={conversationId}
            taskId={task.request_id}
            question={task.input}
          />

          <CopyConversation
            task={task}
            showingSteps={showingSteps}
            questionOnly={false}
            size="xsmall"
            conversationId={conversationId}
          />
          <PrintPdfBtn
            task={task}
            showingSteps={showingSteps}
            conversationId={conversationId}
          />
          {isScheduledTaskEnabled && (
            <TooltipContainer
              label={
                isAtMaxScheduledTasks
                  ? t('chat:historySidebar.scheduleTaskDisabledMaxReached')
                  : t('chat:historySidebar.scheduleTask')
              }
              inPortal
              trigger={[TooltipTrigger.Hover]}
            >
              <Button
                data-testid="schedule-task-button"
                variant="empty"
                size="xsmall"
                iconName="Clock"
                aria-label={t('chat:historySidebar.scheduleTask')}
                disabled={isAtMaxScheduledTasks}
                onClick={() => openScheduleDrawer(task.input, webSearch)}
              />
            </TooltipContainer>
          )}
        </>
      )}
      {(feedback === Feedback.Good || !feedback) && (
        <Button
          data-testid="button-positive-feedback"
          variant="empty"
          size="xsmall"
          iconName="ThumbsUp"
          disabled={feedback === Feedback.Good}
          onClick={onFeedbackButtonClick}
          name={Feedback.Good}
        />
      )}
      {(feedback === Feedback.Bad || !feedback) && (
        <Button
          data-testid="button-negative-feedback"
          variant="empty"
          size="xsmall"
          iconName="ThumbsDown"
          onClick={onFeedbackButtonClick}
          name={Feedback.Bad}
          disabled={feedback === Feedback.Bad}
        />
      )}
    </div>
  );
};
